import React, { useRef, useState } from 'react';
import { Button, Checkbox, Modal, InputNumber } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { QRCodeCanvas } from 'qrcode.react';
import { BlobProvider, Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { ensureArabicFontLoaded, renderArabicBlockToDataUrl } from './ArabicTextImage';

const PT_PER_MM = 72 / 25.4;
const mm = (value) => value * PT_PER_MM;

const LABEL_SPECS = {
  large: { widthMm: 400, heightMm: 70, qrMm: 30, padMm: 5, gapMm: 5, arLineHeight: 1.4 },
  small: { widthMm: 185, heightMm: 85, qrMm: 25, padMm: 5, gapMm: 4, arabicMaxMm: 40, arLineHeight: 1.45 },
};

const EN_LINE_HEIGHT = 1.35;
const CODE_FONT_PT = 9;
const CODE_BLOCK_MM = 6;
const DEFAULT_FONT_PT = 20;

const getArabicWidthMm = (spec, size) =>
  size === 'large'
    ? (spec.widthMm - 2 * spec.padMm - spec.qrMm - 2 * spec.gapMm) / 2
    : spec.widthMm - 2 * spec.padMm;

Font.register({
  family: 'BahijTheSansArabic',
  fonts: [
    { src: '/fonts/BahijTheSansArabic-SemiBold.ttf', fontWeight: 600 },
    { src: '/fonts/BahijTheSansArabic-Bold.ttf', fontWeight: 700 },
  ],
});

Font.register({
  family: 'Roboto',
  fonts: [
    { src: '/fonts/Roboto-Medium.ttf', fontWeight: 500 },
    { src: '/fonts/Roboto-Bold.ttf', fontWeight: 700 },
  ],
});
Font.registerHyphenationCallback((word) => [word]);

// objectTitle/description come from TranslatableInput as { en, ar }; older
// records saved before that migration can still be a plain string, which we
// treat as English rather than showing blank.
const getLangValue = (field, lang) => {
  if (!field) return '';
  if (typeof field === 'string') return lang === 'en' ? field : '';
  return field[lang] || '';
};

const qrOnlyStyles = StyleSheet.create({
  page: {
    width: mm(62), height: mm(29), flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
  },
  qr: { width: mm(20), height: mm(20) },
  code: { marginTop: mm(1), fontSize: 9, fontWeight: 700 },
});

const QrOnlyDoc = ({ qrDataUrl, code }) => (
  <Document>
    <Page size={[qrOnlyStyles.page.width, qrOnlyStyles.page.height]} style={qrOnlyStyles.page}>
      <Image src={qrDataUrl} style={qrOnlyStyles.qr} />
      <Text style={qrOnlyStyles.code}>{code}</Text>
    </Page>
  </Document>
);

const labelStyles = StyleSheet.create({
  page: { backgroundColor: '#f6e8bc', fontFamily: 'Roboto' },
  code: { marginTop: mm(1), fontSize: CODE_FONT_PT, textAlign: 'center', fontFamily: 'Roboto', fontWeight: 500 },
});

const EnglishBlock = ({ title, country, century, description, fontPt, heightMm }) => {
  const subtitle = [country?.en, century?.en].filter(Boolean).join(' – ');
  const linePt = fontPt * EN_LINE_HEIGHT;
  const zonePt = Math.max(1, Math.floor((mm(heightMm) + 2) / linePt)) * linePt + 2;
  return (
    <View style={{ height: zonePt, overflow: 'hidden' }}>
      <Text
        style={{
          fontFamily: 'Roboto', fontSize: fontPt, lineHeight: EN_LINE_HEIGHT,
          fontWeight: 500, textAlign: 'left', flexShrink: 0,
        }}
      >
        <Text style={{ fontWeight: 700 }}>{title.en}</Text>
        {subtitle ? `\n${subtitle}` : ''}
        {description.en ? `\n${description.en}` : ''}
      </Text>
    </View>
  );
};

const QrBlock = ({ qrDataUrl, code, qrMm }) => (
  <View style={{ alignItems: 'center' }}>
    <Image src={qrDataUrl} style={{ width: mm(qrMm), height: mm(qrMm) }} />
    <Text style={labelStyles.code}>{code}</Text>
  </View>
);

const LabelDoc = ({
  qrDataUrl, code, languages, title, description, country, century,
  size, arabicImage, fontPtEn,
}) => {
  const spec = LABEL_SPECS[size];
  const isLarge = size === 'large';
  const contentWmm = spec.widthMm - 2 * spec.padMm;
  const contentHmm = spec.heightMm - 2 * spec.padMm;
  const arabicWidthMm = getArabicWidthMm(spec, size);

  const showEn = languages.includes('en');
  const showAr = languages.includes('ar') && !!arabicImage;
  const arabicHeightMm = showAr ? arabicWidthMm * (arabicImage.heightPx / arabicImage.widthPx) : 0;

  const arabicEl = showAr && (
    <Image
      src={arabicImage.url}
      style={{ width: mm(arabicWidthMm), height: mm(arabicHeightMm), flexShrink: 0 }}
    />
  );

  const english = (heightMm) => (
    <EnglishBlock
      title={title} country={country} century={century} description={description}
      fontPt={fontPtEn} heightMm={heightMm}
    />
  );

  return (
    <Document>
      <Page size={[mm(spec.widthMm), mm(spec.heightMm)]} wrap={false} style={[labelStyles.page, { padding: mm(spec.padMm) }]}>
        {isLarge ? (
          <View style={{ width: mm(contentWmm), height: mm(contentHmm), flexDirection: 'row', alignItems: 'flex-start', gap: mm(spec.gapMm) }}>
            <View style={{ width: mm(arabicWidthMm), height: mm(contentHmm), overflow: 'hidden' }}>
              {showEn && english(contentHmm)}
            </View>
            <View style={{ width: mm(spec.qrMm), height: mm(contentHmm), justifyContent: 'center' }}>
              <QrBlock qrDataUrl={qrDataUrl} code={code} qrMm={spec.qrMm} />
            </View>
            <View style={{ width: mm(arabicWidthMm), height: mm(contentHmm), overflow: 'hidden', alignItems: 'flex-end' }}>
              {arabicEl}
            </View>
          </View>
        ) : (
          (() => {
            const topMm = Math.max(spec.qrMm + CODE_BLOCK_MM, arabicHeightMm);
            const englishMm = contentHmm - topMm - spec.gapMm;
            return (
              <View style={{ width: mm(contentWmm), height: mm(contentHmm) }}>
                <View style={{ position: 'relative', width: mm(contentWmm), height: mm(topMm), flexShrink: 0 }}>
                  {arabicEl}
                  <View style={{ position: 'absolute', top: 0, left: 0 }}>
                    <QrBlock qrDataUrl={qrDataUrl} code={code} qrMm={spec.qrMm} />
                  </View>
                </View>
                {showEn && (
                  <View style={{ marginTop: mm(spec.gapMm), flexShrink: 0 }}>{english(englishMm)}</View>
                )}
              </View>
            );
          })()
        )}
      </Page>
    </Document>
  );
};

// Mirrors GenericFormPdf's BlobProvider-based auto-download: render this once
// a document is ready, it clicks a hidden link for the browser and reports back.
const PdfDownloadTrigger = ({ pdfDoc, fileName, onDone }) => (
  <BlobProvider document={pdfDoc}>
    {({ url, loading }) => {
      if (url && !loading) {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        onDone();
      }
      return null;
    }}
  </BlobProvider>
);

const QrCodeModal = ({ open, setOpen, inventoryNumber, objectTitle, description, country, century }) => {
  const { t } = useTranslation();
  const qrCanvasRef = useRef(null);
  const [languages, setLanguages] = useState(['en', 'ar']);
  const [pdfRequest, setPdfRequest] = useState(null);

  const [pending, setPending] = useState(null); 

  const handleDownloadQr = () => {
    const qrDataUrl = qrCanvasRef.current?.toDataURL('image/png');
    if (!qrDataUrl) return;
    setPdfRequest({
      pdfDoc: <QrOnlyDoc qrDataUrl={qrDataUrl} code={inventoryNumber} />,
      fileName: `${inventoryNumber || 'qr-code'}.pdf`,
    });
  };

  const openFontDialog = (size) => {
    setPending({ size, en: DEFAULT_FONT_PT, ar: DEFAULT_FONT_PT });
  };

  const confirmFontDialog = async () => {
    const { size, en, ar } = pending;
    setPending(null);
    await generateLabel(size, en, ar);
  };

  const generateLabel = async (size, enPt, arPt) => {
    const qrDataUrl = qrCanvasRef.current?.toDataURL('image/png');
    if (!qrDataUrl) return;

    const spec = LABEL_SPECS[size];
    let arabicImage = null;

    if (languages.includes('ar')) {
      await ensureArabicFontLoaded();

      const arabicWidthMm = getArabicWidthMm(spec, size);
      const maxWidthPx = 1400; 
      const pxPerMm = maxWidthPx / arabicWidthMm;
      const fontSize = (arPt * maxWidthPx) / mm(arabicWidthMm);

      const maxHeightMm = size === 'large' ? spec.heightMm - 2 * spec.padMm : spec.arabicMaxMm;

      const floatOpts = size === 'small'
        ? {
            floatWidthPx: (spec.qrMm + spec.gapMm) * pxPerMm,
            floatHeightPx: (spec.qrMm + CODE_BLOCK_MM) * pxPerMm,
          }
        : {};

      arabicImage = renderArabicBlockToDataUrl(
        [
          { text: getLangValue(objectTitle, 'ar'), fontSize, fontWeight: 700 },
          {
            text: [getLangValue(country, 'ar'), getLangValue(century, 'ar')].filter(Boolean).join(' – '),
            fontSize,
            fontWeight: 600,
          },
          { text: getLangValue(description, 'ar'), fontSize, fontWeight: 600 },
        ],
        {
          maxWidthPx,
          maxHeightPx: maxHeightMm * pxPerMm,
          lineHeightRatio: spec.arLineHeight,
          ...floatOpts,
        },
      );
    }

    setPdfRequest({
      pdfDoc: (
        <LabelDoc
          qrDataUrl={qrDataUrl}
          code={inventoryNumber}
          size={size}
          languages={languages}
          title={{ en: getLangValue(objectTitle, 'en') }}
          description={{ en: getLangValue(description, 'en') }}
          country={{ en: getLangValue(country, 'en') }}
          century={{ en: getLangValue(century, 'en') }}
          arabicImage={arabicImage}
          fontPtEn={enPt}
        />
      ),
      fileName: `${inventoryNumber || 'label'}_${size}.pdf`,
    });
  };

  return (
    <Modal
      title={t('objects.qrCode')}
      open={open}
      onCancel={() => setOpen(false)}
      // Default antd footer lays buttons out in one non-wrapping row, so four
      // buttons this wide overflowed past the modal edge. A custom footer
      // groups the two label downloads (primary) above Cancel/QR-only
      // instead, so it stays contained and reads as one action + one utility row.
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              key="download-small"
              type="primary"
              icon={<DownloadOutlined />}
              disabled={!languages.length}
              onClick={() => openFontDialog('small')}
              style={{ flex: 1 }}
            >
              {t('objects.downloadSmallLabel')}
            </Button>
            <Button
              key="download-large"
              type="primary"
              icon={<DownloadOutlined />}
              disabled={!languages.length}
              onClick={() => openFontDialog('large')}
              style={{ flex: 1 }}
            >
              {t('objects.downloadLargeLabel')}
            </Button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button key="close" onClick={() => setOpen(false)} style={{ flex: 1 }}>
              {t('common.cancel')}
            </Button>
            <Button type="primary" key="download-qr" icon={<DownloadOutlined />} onClick={handleDownloadQr} style={{ flex: 1 }}>
              {t('objects.downloadQrCode')}
            </Button>
          </div>
        </div>
      }
      width={480}
      centered
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 0' }}>
        <QRCodeCanvas ref={qrCanvasRef} value={inventoryNumber || ''} size={200} level="H" />
        <div style={{ marginTop: 12, fontSize: 14, fontWeight: 500, color: '#666' }}>
          {inventoryNumber}
        </div>
        <div style={{ marginTop: 20 }}>
          <div style={{ marginBottom: 6, fontSize: 12, color: '#888' }}>
            {t('objects.labelLanguages')}
          </div>
          <Checkbox.Group
            options={[
              { label: t('objects.english'), value: 'en' },
              { label: t('objects.arabic'), value: 'ar' },
            ]}
            value={languages}
            onChange={setLanguages}
          />
        </div>
      </div>

      {pdfRequest && (
        <PdfDownloadTrigger
          pdfDoc={pdfRequest.pdfDoc}
          fileName={pdfRequest.fileName}
          onDone={() => setPdfRequest(null)}
        />
      )}

      <Modal
        title={`${t('objects.setFontSize')} (${pending?.size === 'large' ? t('objects.largeLabel') : t('objects.smallLabel')})`}
        open={!!pending}
        onCancel={() => setPending(null)}
        onOk={confirmFontDialog}
        okText={t('common.download')}
        cancelText={t('common.cancel')}
        width={360}
        centered
        destroyOnClose
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
          {languages.includes('en') && (
            <div>
              <div style={{ marginBottom: 6, fontSize: 13, color: '#555' }}>{t('objects.englishFontSize')}</div>
              <InputNumber
                min={1}
                step={1}
                value={pending?.en}
                onChange={(v) => setPending((p) => (p ? { ...p, en: v ?? DEFAULT_FONT_PT } : p))}
                style={{ width: '100%' }}
              />
            </div>
          )}
          {languages.includes('ar') && (
            <div>
              <div style={{ marginBottom: 6, fontSize: 13, color: '#555' }}>{t('objects.arabicFontSize')}</div>
              <InputNumber
                min={1}
                step={1}
                value={pending?.ar}
                onChange={(v) => setPending((p) => (p ? { ...p, ar: v ?? DEFAULT_FONT_PT } : p))}
                style={{ width: '100%' }}
              />
            </div>
          )}
        </div>
      </Modal>
    </Modal>
  );
};

export default QrCodeModal;